import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useHasWorkspaceModule } from '@/hooks/useHasWorkspaceModule';
import { usePortalSummaryQuery } from '@/hooks/usePortalSummaryQuery';
import { extractFirstName } from '@/utils/personName';
import { ClinicContactCard } from '../ClinicContactCard';
import { LatestAnnouncementsCard } from '../LatestAnnouncementsCard';
import { PortalPageHeader } from '../PortalPageHeader';
import { ReviewCard } from '../ReviewCard';
import { buildNextSteps } from './buildNextSteps';
import { NextStepsCard } from './NextStepsCard';
import { NextVisitCard } from './NextVisitCard';
import { YourPetsCard } from './YourPetsCard';

export function HomePage() {
  const { t } = useTranslation('clients');
  const summaryQuery = usePortalSummaryQuery();
  const hasChatModule = useHasWorkspaceModule('chat');

  const summary = summaryQuery.data;
  const isSummaryLoading = summaryQuery.isLoading;

  const pets = summary === undefined ? [] : summary.pets;
  const intakeSubmissions = summary === undefined ? [] : summary.intakeSubmissions;
  const petsWithExercisesDueToday = summary === undefined ? [] : summary.petsWithExercisesDueToday;
  const consentsNeedingReacceptanceCount =
    summary === undefined ? 0 : summary.consentsNeedingReacceptanceCount;
  const unreadMessageCount =
    hasChatModule === false || summary === undefined ? 0 : summary.unreadChatCount;

  const nextSteps = buildNextSteps({
    pets,
    intakeSubmissions,
    petsWithExercisesDueToday,
    unreadMessageCount,
    consentsNeedingReacceptanceCount,
  });

  if (isSummaryLoading === true || summary === undefined) {
    return (
      <div className="p-4 sm:p-6 md:p-8 py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <PortalPageHeader
        title={t('portal.greeting', { name: extractFirstName(summary.clientFullName) })}
        helperText={t('portal.homeSubtitle')}
      />
      <div className="flex flex-col gap-5 lg:grid lg:grid-cols-3 lg:items-start">
        <div className="contents lg:flex lg:flex-col lg:gap-5 lg:col-span-2">
          <div className="order-1 lg:order-none">
            <NextStepsCard items={nextSteps} isLoading={false} />
          </div>
          <div className="order-4 lg:order-none">
            <YourPetsCard pets={pets} isLoading={false} />
          </div>
        </div>
        <div className="contents lg:flex lg:flex-col lg:gap-5">
          <div className="order-2 lg:order-none">
            <NextVisitCard nextVisit={summary.nextVisit} isLoading={false} />
          </div>
          <div className="order-3 lg:order-none empty:hidden">
            <LatestAnnouncementsCard
              announcements={summary.latestAnnouncements}
              totalAnnouncementsCount={summary.totalAnnouncementsCount}
            />
          </div>
          <div className="order-5 lg:order-none empty:hidden">
            <ClinicContactCard clinicContact={summary.clinicContact} />
          </div>
          <div className="order-6 lg:order-none empty:hidden">
            <ReviewCard />
          </div>
        </div>
      </div>
    </div>
  );
}
